import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, GitBranch, Package, Store, ShoppingCart, Truck, Boxes, FileText, LogOut, Leaf, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const adminNav = [
  { to: "/portal/admin", label: "Dashboard", icon: LayoutDashboard },
  { to: "/portal/pipeline", label: "Franchise Pipeline", icon: GitBranch },
  { to: "/portal/products", label: "Products", icon: Package },
  { to: "/portal/stores", label: "Stores", icon: Store },
  { to: "/portal/orders", label: "Orders", icon: ShoppingCart },
  { to: "/portal/suppliers", label: "Suppliers", icon: Truck },
];

const franchiseNav = [
  { to: "/portal/store", label: "Dashboard", icon: LayoutDashboard },
  { to: "/portal/inventory", label: "Inventory", icon: Boxes },
  { to: "/portal/purchase-orders", label: "Purchase Orders", icon: FileText },
];

const roleLabel = {
  super_admin: "Super Admin", head_office: "Head Office",
  franchise_owner: "Franchise Owner", store_manager: "Store Manager", customer: "Customer",
};

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isAdmin = ["super_admin", "head_office"].includes(user?.role);
  const nav = isAdmin ? adminNav : franchiseNav;

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-slate-900 text-slate-300 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center gap-2 px-5 border-b border-slate-800">
          <Link to="/" className="flex items-center gap-2">
            <span className="grid place-items-center w-8 h-8 rounded-lg bg-yellow-400 text-slate-900"><Leaf className="w-4 h-4" /></span>
            <span className="font-heading font-extrabold text-white">D K <span className="text-yellow-400">BAZAAR</span></span>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end onClick={() => setOpen(false)} data-testid={`side-${n.label.replace(/\s/g, "-").toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-yellow-400 text-slate-900" : "hover:bg-slate-800 hover:text-white"}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="px-3 py-2">
            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
            <p className="text-xs text-yellow-400">{roleLabel[user?.role]}</p>
          </div>
          <Button onClick={doLogout} data-testid="logout-btn" variant="ghost" className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800">
            <LogOut className="w-4 h-4 mr-2" /> Sign Out
          </Button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
          <button className="lg:hidden p-2" onClick={() => setOpen((o) => !o)}>{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
          <h1 className="font-heading font-semibold text-slate-900 dark:text-white hidden sm:block">{isAdmin ? "Head Office Console" : "Store Console"}</h1>
          <Button asChild variant="outline" size="sm"><Link to="/">View Site</Link></Button>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
